import React, { useMemo, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import { Translate } from 'react-localize-redux';
import LoadingState from './common/LoadingState';
import ErrorStateView from './common/ErrorState';

export interface InvestigationComparisonRow {
    uuid: string;
    name: string;
    patients: number;
    closedVisits: number;
    lesions: number;
    images: number;
    primaryDiseaseRoot: string | null;
    primaryDiseaseLabel: string | null;
    labConfirmationRate: number;
}

export interface InvestigationComparisonTablePureProps {
    loading: boolean;
    error?: Error | null;
    rows: InvestigationComparisonRow[];
    onRetry?: () => void;
}

type SortKey =
    | 'name'
    | 'patients'
    | 'closedVisits'
    | 'lesions'
    | 'images'
    | 'primaryDisease'
    | 'labConfirmationRate';

type SortOrder = 'asc' | 'desc';

const primaryDiseaseDisplay = (row: InvestigationComparisonRow): string =>
    row.primaryDiseaseLabel || row.primaryDiseaseRoot || '—';

const formatLabRate = (rate: number): string =>
    `${(rate * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

const compareRows = (
    a: InvestigationComparisonRow,
    b: InvestigationComparisonRow,
    orderBy: SortKey,
    order: SortOrder,
): number => {
    let cmp = 0;
    switch (orderBy) {
        case 'name':
            cmp = a.name.localeCompare(b.name);
            break;
        case 'primaryDisease':
            cmp = primaryDiseaseDisplay(a).localeCompare(primaryDiseaseDisplay(b));
            break;
        case 'labConfirmationRate':
            cmp = a.labConfirmationRate - b.labConfirmationRate;
            break;
        default:
            cmp = a[orderBy] - b[orderBy];
    }
    return order === 'asc' ? cmp : -cmp;
};

export const InvestigationComparisonTablePure: React.FC<InvestigationComparisonTablePureProps> = ({
    loading,
    error,
    rows,
    onRetry,
}) => {
    const [orderBy, setOrderBy] = useState<SortKey>('name');
    const [order, setOrder] = useState<SortOrder>('asc');

    const sortedRows = useMemo(
        () => [...rows].sort((a, b) => compareRows(a, b, orderBy, order)),
        [rows, orderBy, order],
    );

    const handleSort = (column: SortKey) => {
        const isAsc = orderBy === column && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(column);
    };

    if (loading) {
        return (
            <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    <Translate id="hospital.analytics.studies.loading" />
                </Typography>
                <LoadingState />
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    <Translate id="hospital.analytics.studies.error" />
                </Typography>
                <ErrorStateView error={error} onRetry={onRetry} />
            </Box>
        );
    }

    if (!rows.length) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                    <Translate id="hospital.analytics.studies.empty" />
                </Typography>
            </Box>
        );
    }

    const sortDirection = (column: SortKey): 'asc' | 'desc' | false =>
        orderBy === column ? order : false;

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sortDirection={sortDirection('name')}>
                            <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={() => handleSort('name')}
                            >
                                <Translate id="hospital.analytics.studies.columns.name" />
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={sortDirection('patients')}>
                            <TableSortLabel
                                active={orderBy === 'patients'}
                                direction={orderBy === 'patients' ? order : 'asc'}
                                onClick={() => handleSort('patients')}
                            >
                                <Translate id="hospital.analytics.studies.columns.patients" />
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={sortDirection('closedVisits')}>
                            <TableSortLabel
                                active={orderBy === 'closedVisits'}
                                direction={orderBy === 'closedVisits' ? order : 'asc'}
                                onClick={() => handleSort('closedVisits')}
                            >
                                <Translate id="hospital.analytics.studies.columns.closed-visits" />
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={sortDirection('lesions')}>
                            <TableSortLabel
                                active={orderBy === 'lesions'}
                                direction={orderBy === 'lesions' ? order : 'asc'}
                                onClick={() => handleSort('lesions')}
                            >
                                <Translate id="hospital.analytics.studies.columns.lesions" />
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={sortDirection('images')}>
                            <TableSortLabel
                                active={orderBy === 'images'}
                                direction={orderBy === 'images' ? order : 'asc'}
                                onClick={() => handleSort('images')}
                            >
                                <Translate id="hospital.analytics.studies.columns.images" />
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortDirection('primaryDisease')}>
                            <TableSortLabel
                                active={orderBy === 'primaryDisease'}
                                direction={orderBy === 'primaryDisease' ? order : 'asc'}
                                onClick={() => handleSort('primaryDisease')}
                            >
                                <Translate id="hospital.analytics.studies.columns.primary-disease" />
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={sortDirection('labConfirmationRate')}>
                            <TableSortLabel
                                active={orderBy === 'labConfirmationRate'}
                                direction={orderBy === 'labConfirmationRate' ? order : 'asc'}
                                onClick={() => handleSort('labConfirmationRate')}
                            >
                                <Translate id="hospital.analytics.studies.columns.lab-confirmation" />
                            </TableSortLabel>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedRows.map(row => (
                        <TableRow key={row.uuid} hover>
                            <TableCell>{row.name}</TableCell>
                            <TableCell align="right">{row.patients.toLocaleString()}</TableCell>
                            <TableCell align="right">{row.closedVisits.toLocaleString()}</TableCell>
                            <TableCell align="right">{row.lesions.toLocaleString()}</TableCell>
                            <TableCell align="right">{row.images.toLocaleString()}</TableCell>
                            <TableCell>{primaryDiseaseDisplay(row)}</TableCell>
                            <TableCell align="right">{formatLabRate(row.labConfirmationRate)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const InvestigationComparisonTable = InvestigationComparisonTablePure;
export default InvestigationComparisonTable;
